import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Building2, Users, UserCheck, Wallet, Calculator, LogOut, PieChart, Share2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMonth } from "@/context/MonthContext";
import { monthLabel } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const linksAll = [
  { to: "/", label: "Konsolide Özet", icon: LayoutDashboard, end: true },
  { to: "/firmalar", label: "Firmalar", icon: Building2 },
  { to: "/yoneticiler", label: "Çalışanlar (Yönetim)", icon: UserCheck },
  { to: "/calisanlar", label: "Şirket Denetçileri", icon: Users },
  { to: "/islemler", label: "Gelir & Gider", icon: Wallet },
  { to: "/yonetici-giderleri", label: "Ortak Giderler", icon: Calculator },
  { to: "/ortaklar", label: "Ortak Payları", icon: PieChart },
  { to: "/dagitim", label: "Dağıtım", icon: Share2 },
];

const linksAccountant = [
  { to: "/islemler", label: "Gelir & Gider", icon: Wallet, end: true },
];

function monthOptions() {
  // Yıl 2035'e kadar — kullanıcı 10 yıl boyunca ay seçebilsin
  const out = [];
  const start = new Date(2024, 0, 1);        // Ocak 2024
  const end = new Date(2035, 11, 1);         // Aralık 2035
  const cur = new Date(end);
  while (cur >= start) {
    const ym = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}`;
    out.push(ym);
    cur.setMonth(cur.getMonth() - 1);
  }
  return out;
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { month, setMonth } = useMonth();
  const nav = useNavigate();

  const links = user?.role === "accountant" ? linksAccountant : linksAll;

  const roleBadge = {
    admin: { label: "Admin", cls: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30" },
    partner: { label: "Ortak", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
    employee: { label: "Çalışan", cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
    accountant: { label: "Muhasebe", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  }[user?.role] || { label: "Kullanıcı", cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" };

  return (
    <div className="min-h-screen flex fk-grain" data-testid="app-shell">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-white/5 bg-[#0b0f17] sticky top-0 h-screen flex flex-col">
        <div className="px-5 pt-6 pb-5 border-b border-white/5">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                 style={{ background: "rgba(99,102,241,0.18)", border: "1px solid rgba(99,102,241,0.35)" }}>
              <Building2 className="w-4.5 h-4.5 text-indigo-300" />
            </div>
            <div>
              <div className="text-sm font-semibold text-white fk-heading">Yapı Denetim</div>
              <div className="text-[10px] text-slate-500 font-mono uppercase tracking-widest">Firma Paneli</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {links.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end}
              data-testid={`nav-${to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition
                 ${isActive
                    ? "bg-indigo-500/10 text-indigo-200 border border-indigo-500/25"
                    : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"}`
              }>
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 pb-4 pt-2 border-t border-white/5">
          <div className="px-3 py-2.5 rounded-lg bg-white/5 flex items-center justify-between gap-2 mb-2">
            <div className="min-w-0">
              <div className="text-xs text-white truncate">{user?.name}</div>
              <div className="text-[10px] text-slate-500 truncate">{user?.email}</div>
            </div>
            <span className={`fk-pill border ${roleBadge.cls}`}>{roleBadge.label}</span>
          </div>
          <Button variant="ghost" size="sm" data-testid="logout-btn"
                  onClick={() => { logout(); nav("/login"); }}
                  className="w-full justify-start text-slate-400 hover:text-white hover:bg-white/5">
            <LogOut className="w-4 h-4 mr-2" /> Çıkış Yap
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#0b0f17]/80 border-b border-white/5 px-6 py-3.5 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono text-slate-500 uppercase tracking-[.2em]">Aktif Dönem</div>
            <div className="text-base font-semibold text-white fk-heading">{monthLabel(month)}</div>
          </div>
          <div className="flex items-center gap-3">
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger data-testid="month-picker-select"
                             className="w-[180px] h-9 bg-slate-900/60 border-slate-700 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {monthOptions().map((ym) => (
                  <SelectItem key={ym} value={ym}>{monthLabel(ym)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </header>

        <div className="px-6 py-6 relative z-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
